import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import {Col, Form} from 'react-bootstrap';
import {Size} from 'shared/components/Forms/Size';
import {FormGroup} from 'shared/components/Forms/FormGroup';
import {useNotificationContext} from 'contexts/NotificationContext';
import {validationErrorAppliesToField} from 'shared/validator';
import {useFaceDraft} from 'components/customer/Product/Drawer/FaceDraftContext';
import {toNumber} from 'components/customer/Product/Drawer/helpers';

interface FaceHeightFieldProps {
    index: number;
    fieldName: string;
    value: string | number;
    disabled?: boolean;
    // half-typed text goes to the shared draft and only the blur commits, for
    // the layouts that draw the face beside the field
    sharesDraft?: boolean;
    // sits inside the control column so the row still lines up
    trailing?: React.ReactNode;
    onCommit: (index: number, height: number) => void;
}

// Face Height for one face, wherever the layout happens to keep it. Where it
// reads from and what committing means are the caller's business.
export const FaceHeightField = ({
    index,
    fieldName,
    value,
    disabled = false,
    sharesDraft = false,
    trailing = null,
    onCommit,
}: FaceHeightFieldProps) => {
    const {messages} = useNotificationContext();
    const {textFor, setDraft, clearDraft, accept, isHeld, save} =
        useFaceDraft();
    const draft = sharesDraft ? textFor(index) : null;

    const typeHandler = useCallback(
        (name: string, next: number) => {
            if (sharesDraft) {
                setDraft(index, String(next ?? ''));

                return;
            }

            onCommit(index, String(next ?? '').trim() === '' ? 0 : next);
        },
        [index, onCommit, setDraft, sharesDraft]
    );

    const commitHandler = useCallback(() => {
        if (!sharesDraft) return;

        const text = textFor(index);

        clearDraft();

        if (text === null) return;

        const height = parseFloat(String(text).trim());
        const next = height > 0 ? height : 0;
        const current = toNumber(value);

        // leaving the field untouched is not an edit
        if (!accept(index, next, String(text), current) || next === current) {
            return;
        }

        onCommit(index, next);
    }, [accept, clearDraft, index, onCommit, sharesDraft, textFor, value]);

    // Enter applies the height, then saves the product as other fields do,
    // unless the height does not fit. FormControl's own save on Enter's keyup
    // is held back: it would go before the height is applied.
    const keyHandler = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!sharesDraft || event.key !== 'Enter') return;

            event.preventDefault();
            (event.target as HTMLElement).blur();

            if (!isHeld(index)) save();
        },
        [index, isHeld, save, sharesDraft]
    );

    const keyUpHandler = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (sharesDraft && event.key === 'Enter') event.stopPropagation();
        },
        [sharesDraft]
    );

    // passed on rather than re-resolved by FormControl, which indexes
    // differently and can end up with an empty error tooltip
    const {isInvalid, errorMessage} = useMemo(() => {
        const errors = messages?.errors;

        if (errors && Array.isArray(errors)) {
            const match = errors.find((error) =>
                validationErrorAppliesToField(
                    error,
                    'drawer_face_height',
                    index
                )
            );

            return {isInvalid: !!match, errorMessage: match?.message};
        }

        return {isInvalid: false, errorMessage: undefined};
    }, [messages, index]);

    return (
        <FormGroup>
            <Form.Label column xs={5}>
                Face Height
                <span>*</span>
            </Form.Label>
            <Col xs={7}>
                <Control
                    onBlur={commitHandler}
                    onKeyDown={keyHandler}
                    onKeyUpCapture={keyUpHandler}>
                    <Size
                        name={fieldName}
                        value={draft === null ? value : draft}
                        metric="mm"
                        selectHandler={typeHandler}
                        disabled={disabled}
                        isInvalid={isInvalid}
                        error={errorMessage}
                        fieldSetIndex={index}
                        liveUpdate={sharesDraft}
                    />
                    {trailing}
                </Control>
            </Col>
        </FormGroup>
    );
};

const Control = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    > .input-group {
        flex: 1;
    }
`;
